import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, Download, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';

const PAGE_SIZE = 50;

export function ParentHistory() {
  const { headers, user } = useAuthContext();
  const { t, i18n } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterDirection, setFilterDirection] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const locale = i18n.language === 'sv' ? 'sv-SE' : 'en-US';

  const fetchTransactions = async () => {
    try {
      let url = `${API}/transaction?limit=${PAGE_SIZE}&offset=${(page - 1) * PAGE_SIZE}`;
      if (filterDirection !== 'all') {
        url += `&direction=${filterDirection}`;
      }
      if (filterCategory !== 'all') {
        url += `&category=${filterCategory}`;
      }
      
      const res = await axios.get(url, { headers });
      setTransactions(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error('Failed to fetch transactions:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/p2p-category?lang=${(i18n.language || 'en').startsWith('sv') ? 'sv' : 'en'}`, { headers });
      setCategories(res.data?.items || []);
    } catch (e) {}
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterDirection, filterCategory]);

  useEffect(() => {
    fetchTransactions();
  }, [filterDirection, filterCategory, page, headers.Authorization]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/transaction/export?format=csv`, { headers });
      const blob = new Blob([res.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paymig_history_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (e) {
      console.error('Export error:', e);
    }
  };

  const openTransactionDetail = (txn) => {
    setSelectedTransaction(txn);
    setDetailDialogOpen(true);
  };

  const formatTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDateTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString(locale, { 
        year: 'numeric',
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  const formatDateFull = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(locale, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'Unknown Date';
    }
  };

  // Group by date
  const groupedTransactions = transactions.reduce((groups, txn) => {
    const date = formatDateFull(txn.created_at || txn.completed_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(txn);
    return groups;
  }, {});

  const getTypeLabel = (type) => {
    const labels = {
      'DIRECT_PAYMENT': t('history.payment'),
      'P2P_PAYMENT': t('history.p2pPayment'),
      'GIFT': t('history.gift'),
      'BUDDY_REQUEST': t('history.buddyRequest'),
      'TRANSFER': t('history.transfer'),
      'ALLOWANCE': t('history.allowance'),
      'SCHEDULED_PAYMENT': t('history.scheduledPayment')
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    if (status === 'COMPLETED' || status === 'PAID') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />{t('history.statusCompleted')}</Badge>;
    } else if (status === 'PENDING') {
      return <Badge className="bg-orange-100 text-orange-700"><Clock className="h-3 w-3 mr-1" />{t('history.statusPending')}</Badge>;
    } else if (status === 'CANCELLED' || status === 'DECLINED') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />{t('history.statusCancelled')}</Badge>;
    } else if (status === 'ERROR') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />{t('history.statusError')}</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };
  
  return (
    <div className="space-y-6" data-testid="parent-history">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('nav.history')}</h2>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-1" />
          {t('common.export')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterDirection} onValueChange={setFilterDirection}>
          <SelectTrigger className="w-[140px]" data-testid="filter-direction">
            <SelectValue placeholder={t('common.filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="sent">{t('common.sent')}</SelectItem>
            <SelectItem value="received">{t('common.received')}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[140px]" data-testid="filter-category">
            <SelectValue placeholder={t('payer.category')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {categories.map(c => (
              <SelectItem key={c._id || c.id} value={c.code}>
                {c.icon} {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Transaction List */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      ) : Object.keys(groupedTransactions).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">{t('history.noTransactions')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('history.historyAppearHere')}
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedTransactions).map(([date, items]) => (
          <div key={date} className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground py-2" data-testid="history-date-header">
              {date}
            </h3>
            <div className="space-y-2">
              {items.map((txn) => {
                const currentUserId = user?.id;
                const isSent = txn.from_user_id === currentUserId;
                const counterparty = isSent ? txn.to_name : txn.from_name;
                
                return (
                  <Card 
                    key={txn._id || txn.id} 
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openTransactionDetail(txn)}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${isSent ? 'bg-red-100' : 'bg-green-100'}`}>
                          {isSent ? (
                            <ArrowUpRight className="h-4 w-4 text-red-600" />
                          ) : (
                            <ArrowDownLeft className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="truncate">
                              <p className="font-medium text-sm truncate">
                                {counterparty || 'Unknown'}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {getStatusBadge(txn.status)}
                                {txn.category && (
                                  <Badge variant="secondary" className="text-[10px]">
                                    {txn.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2 flex items-center gap-2">
                              <div>
                                <p className={`font-semibold ${isSent ? 'text-red-600' : 'text-green-600'}`}>
                                  {isSent ? '-' : '+'}{txn.amount} {txn.currency || 'SEK'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatTime(txn.created_at || txn.completed_at)}
                                </p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                          {txn.description && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {txn.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2" data-testid="history-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="history-page-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('common.page', { defaultValue: 'Page' })} {page} / {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="history-page-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Transaction Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              {/* Amount */}
              <div className="text-center py-4 bg-gray-50 rounded-lg">
                <p className={`text-3xl font-bold ${
                  selectedTransaction.from_user_id === user?.id ? 'text-red-600' : 'text-green-600'
                }`}>
                  {selectedTransaction.from_user_id === user?.id ? '-' : '+'}
                  {selectedTransaction.amount} {selectedTransaction.currency || 'SEK'}
                </p>
                <div className="mt-2">
                  {getStatusBadge(selectedTransaction.status)}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{getTypeLabel(selectedTransaction.type)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {selectedTransaction.from_user_id === user?.id ? t('requests.to') : t('requests.from')}
                  </span>
                  <span className="font-medium">
                    {selectedTransaction.from_user_id === user?.id 
                      ? selectedTransaction.to_name 
                      : selectedTransaction.from_name}
                  </span>
                </div>

                {selectedTransaction.to_alias && selectedTransaction.from_user_id === user?.id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('history.swishNumber')}</span>
                    <span className="font-medium">{selectedTransaction.to_alias}</span>
                  </div>
                )}

                {selectedTransaction.from_alias && selectedTransaction.from_user_id !== user?.id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('history.swishNumber')}</span>
                    <span className="font-medium">{selectedTransaction.from_alias}</span>
                  </div>
                )}

                {selectedTransaction.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('history.description')}</span>
                    <span className="font-medium text-right max-w-[60%]">{selectedTransaction.description}</span>
                  </div>
                )}

                {selectedTransaction.category && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('history.category')}</span>
                    <Badge variant="secondary">{selectedTransaction.category}</Badge>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('history.date')}</span>
                  <span className="font-medium">{formatDateTime(selectedTransaction.created_at)}</span>
                </div>

                {selectedTransaction.completed_at && selectedTransaction.completed_at !== selectedTransaction.created_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('history.completed')}</span>
                    <span className="font-medium">{formatDateTime(selectedTransaction.completed_at)}</span>
                  </div>
                )}

                {selectedTransaction.manual_completion && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Confirmation</span>
                    <Badge variant="outline">{t('history.manual')}</Badge>
                  </div>
                )}
              </div>

              {/* Transaction ID */}
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground text-center">
                  {t('history.transactionId')}: {selectedTransaction._id || selectedTransaction.id}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
